import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { Info, Lock } from "lucide-react";

import { UIActionModal, UICallout, UISelect } from "../../../../ui";

const isSameId = (first, second) =>
  first !== undefined &&
  first !== null &&
  second !== undefined &&
  second !== null &&
  String(first) === String(second);

const getAssignedZone = (row, zoneOptions = []) => {
  if (row.zone || row.zoneId) return row.zone;
  if (!row.isSupervisor) return "";

  return (
    zoneOptions.find((option) => isSameId(option.supervisorId, row.userId))
      ?.name || ""
  );
};

const isApplicantAssigned = (row, zoneOptions) =>
  Boolean(row.zone || row.zoneId || getAssignedZone(row, zoneOptions));

const getApplicantStatus = (row, copy, zoneOptions) =>
  isApplicantAssigned(row, zoneOptions)
    ? copy.assignedStatus
    : copy.unassignedStatus;

const AllocateRequestModal = ({
  open,
  applicants,
  zoneOptions,
  copy,
  onClose,
  onApprove,
  onZonesTabClick,
}) => {
  const [zone, setZone] = useState(null);
  const [step, setStep] = useState("select");

  useEffect(() => {
    if (!open) return;
    setZone(null);
    setStep("select");
  }, [open]);

  const regularApplicants = useMemo(
    () => applicants.filter((item) => !item.isSupervisor),
    [applicants],
  );
  const supervisorApplicants = useMemo(
    () => applicants.filter((item) => item.isSupervisor),
    [applicants],
  );
  const isBulk = applicants.length > 1;
  const isMixed = regularApplicants.length > 0 && supervisorApplicants.length > 0;
  const isSupervisorOnly =
    supervisorApplicants.length > 0 && regularApplicants.length === 0;
  const canAllocateSingleSupervisor =
    isSupervisorOnly && supervisorApplicants.length === 1;
  const allocatableApplicants = canAllocateSingleSupervisor
    ? supervisorApplicants
    : regularApplicants;
  const primaryApplicant = applicants[0];
  const selectedCount = allocatableApplicants.length;
  const assignedCount = allocatableApplicants.filter((row) =>
    isApplicantAssigned(row, zoneOptions),
  ).length;
  const unassignedCount = Math.max(selectedCount - assignedCount, 0);
  const hasMixedAssignment = assignedCount > 0 && unassignedCount > 0;
  const message = isBulk
    ? copy.bulkMessage.replace("{count}", selectedCount)
    : copy.singleMessage;
  const selectedSummaryTemplate =
    isMixed && selectedCount === 1
      ? copy.selectedRegularSingularTemplate
      : isMixed
        ? copy.selectedRegularTemplate
        : copy.selectedTemplate;
  const needsBulkRegularConfirmation =
    isBulk && regularApplicants.length === applicants.length;
  const isConfirmStep = step === "confirm";
  const showSupervisorZonesHint =
    supervisorApplicants.length > 0 &&
    !canAllocateSingleSupervisor &&
    !isConfirmStep;
  const isZoneSelectDisabled =
    isSupervisorOnly && !canAllocateSingleSupervisor;

  const disabled = !zone?.id || selectedCount === 0;
  const closeModal = () => {
    setStep("select");
    onClose();
  };
  const handleConfirmClick = () => {
    if (needsBulkRegularConfirmation && !isConfirmStep) {
      setStep("confirm");
      return;
    }

    onApprove(
      zone,
      allocatableApplicants.map((row) => row.requestId ?? row.id),
    );
  };

  return (
    <UIActionModal
      open={open}
      onClose={closeModal}
      title={isConfirmStep ? copy.confirmTitle : copy.title}
      maxWidth="sm"
      PaperProps={{ className: "event-applicants-allocate-modal" }}
      actions={
        <UIActionModal.Actions
          cancelLabel={isConfirmStep ? copy.back : copy.cancel}
          confirmLabel={copy.approve}
          confirmDisabled={disabled}
          onCancel={() => (isConfirmStep ? setStep("select") : closeModal())}
          onConfirm={handleConfirmClick}
        />
      }
    >
      <Typography className="event-applicants-allocate-modal__message">
        {message}
      </Typography>
      {isMixed && (
        <Typography className="event-applicants-allocate-modal__mixed-message">
          {copy.mixedSelectionMessage}
        </Typography>
      )}

      {isConfirmStep ? (
        <>
          <UIActionModal.Divider />
          <Box className="event-applicants-allocate-modal__summary event-applicants-allocate-modal__summary--confirm">
            <Typography className="event-applicants-allocate-modal__summary-title">
              {zone?.name}
            </Typography>
            <Typography className="event-applicants-allocate-modal__summary-subtitle">
              {copy.allAllocatedTemplate.replace("{count}", selectedCount)}
            </Typography>
            <Typography className="event-applicants-allocate-modal__summary-subtitle">
              {copy.breakdownTemplate
                .replace("{assigned}", assignedCount)
                .replace("{unassigned}", unassignedCount)}
            </Typography>
          </Box>
        </>
      ) : !isBulk && primaryApplicant ? (
        <UIActionModal.UserInfo
          name={`${primaryApplicant.name}${primaryApplicant.isSupervisor ? ` - ${copy.supervisor}` : ""}`}
          image={primaryApplicant.image}
          description={`#${primaryApplicant.userId || primaryApplicant.requestId || primaryApplicant.id} - ${getApplicantStatus(primaryApplicant, copy, zoneOptions)}`}
        />
      ) : (
        <>
          <UIActionModal.Divider />
          <Box className="event-applicants-allocate-modal__summary">
            <Typography className="event-applicants-allocate-modal__summary-title">
              {selectedSummaryTemplate.replace("{count}", selectedCount)}
            </Typography>
            {hasMixedAssignment && (
              <Typography className="event-applicants-allocate-modal__summary-subtitle">
                {copy.mixedAssignment}
              </Typography>
            )}
            <Typography className="event-applicants-allocate-modal__summary-subtitle">
              {copy.breakdownTemplate
                .replace("{assigned}", assignedCount)
                .replace("{unassigned}", unassignedCount)}
            </Typography>
          </Box>
        </>
      )}

      {!isConfirmStep && (
        <UISelect
          label={copy.zone}
          placeholder={copy.selectZone}
          value={zone}
          options={zoneOptions}
          onChange={setZone}
          disabled={isZoneSelectDisabled}
          disablePortal
          componentsProps={{
            paper: {
              className: "event-applicants-allocate-modal__zone-paper",
            },
            popper: {
              className: "event-applicants-allocate-modal__zone-popper",
            },
          }}
          getOptionLabel={(option) => option?.name || ""}
          getOptionDisabled={(option) =>
            canAllocateSingleSupervisor &&
            Boolean(option.supervisorName) &&
            !isSameId(option.supervisorId, primaryApplicant?.userId)
          }
          renderOption={(props, option) => {
            const isLocked =
              canAllocateSingleSupervisor &&
              Boolean(option.supervisorName) &&
              !isSameId(option.supervisorId, primaryApplicant?.userId);

            return (
              <li {...props}>
                <span className="event-applicants-allocate-modal__zone-option">
                  <span className="event-applicants-allocate-modal__zone-name">
                    {option.name}
                  </span>
                  {isLocked && (
                    <span className="event-applicants-allocate-modal__zone-lock">
                      {option.supervisorName}
                      <Lock size={13} strokeWidth={1.5} />
                    </span>
                  )}
                </span>
              </li>
            );
          }}
        />
      )}

      {showSupervisorZonesHint && (
        <UICallout
          variant="error"
          className="event-applicants-allocate-modal__warning"
          icon={
            <Info
              size={20}
              strokeWidth={2}
              className="ui-callout__icon"
            />
          }
          title={copy.supervisorsNoAction.replace(
            "{count}",
            supervisorApplicants.length,
          )}
          description={
            <>
              {copy.supervisorsHintPrefix}
              <span className="ui-callout__emphasis">
                {copy.supervisorsHintEmphasis}
              </span>
              {copy.supervisorsHintSuffix}
            </>
          }
          actionLabel={copy.zonesTab}
          onAction={onZonesTabClick}
        />
      )}
    </UIActionModal>
  );
};

AllocateRequestModal.propTypes = {
  open: PropTypes.bool.isRequired,
  applicants: PropTypes.arrayOf(PropTypes.object),
  zoneOptions: PropTypes.arrayOf(PropTypes.object),
  copy: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onApprove: PropTypes.func.isRequired,
  onZonesTabClick: PropTypes.func,
};

AllocateRequestModal.defaultProps = {
  applicants: [],
  zoneOptions: [],
  onZonesTabClick: undefined,
};

export default AllocateRequestModal;
